import type { PeniellyItem } from "@rhema/client";
import { Button, CheckIcon, cn, focusRing, PlayIcon, SearchField } from "@rhema/ui";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { LinkForm } from "./LinkForm";
import { useLinkStatus, useLinks } from "./useLinks";

/** Most photos sent from Penielly in one go. */
export const MAX_PICK = 10;

function takenOn(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** Search Penielly and pick photos to send. They go out as normal encrypted Rhema photos. */
export function PeniellyPicker({
  recipient,
  onSend,
  onBack,
}: {
  recipient: string;
  onSend: (items: PeniellyItem[]) => void;
  onBack: () => void;
}) {
  const links = useLinks();
  const { status, enabled, error: statusError } = useLinkStatus("penielly");
  const linked = Boolean(status?.linked);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<PeniellyItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<PeniellyItem[]>([]);

  const run = useCallback(
    (q: string) => {
      setError(null);
      const job = q.trim()
        ? links.penielly.search({ q: q.trim(), limit: 60 }).then((r) => r.items)
        : links.penielly.timeline({ limit: 60 }).then((r) => r.items);
      job.then(setItems).catch((e: Error) => setError(e.message));
    },
    [links],
  );

  useEffect(() => {
    if (linked) run("");
  }, [linked, run]);

  const search = (e: FormEvent) => {
    e.preventDefault();
    setItems(null);
    run(query);
  };

  const toggle = (item: PeniellyItem) =>
    setPicked((list) =>
      list.some((p) => p.id === item.id)
        ? list.filter((p) => p.id !== item.id)
        : list.length >= MAX_PICK
          ? list
          : [...list, item],
    );

  const back = (
    <Button variant="ghost" onClick={onBack}>
      Back
    </Button>
  );

  if (enabled === false) {
    return (
      <div className="mt-3 flex flex-col gap-3">
        <p className="m-0 text-[15px] text-ink-secondary">Connected apps are turned off on this server.</p>
        {back}
      </div>
    );
  }
  if (!status) {
    return (
      <div className="mt-3 flex flex-col gap-3">
        <p className="m-0 text-sm text-ink-secondary" role="status">
          {statusError ?? "Checking Penielly."}
        </p>
        {back}
      </div>
    );
  }
  if (!status.available) {
    return (
      <div className="mt-3 flex flex-col gap-3">
        <p className="m-0 text-[15px] text-ink-secondary">Penielly cannot be reached right now.</p>
        {back}
      </div>
    );
  }
  if (!linked) {
    return (
      <div className="mt-3 flex flex-col gap-3">
        <p className="m-0 text-[15px] leading-normal text-ink">
          Link your Penielly account to send photos from your library.
        </p>
        <LinkForm product="penielly" onCancel={onBack} />
      </div>
    );
  }

  const count = picked.length;
  return (
    <div className="mt-3 flex flex-col gap-3">
      <form onSubmit={search}>
        <SearchField
          label="Search Penielly"
          placeholder="Search your photos"
          value={query}
          onChange={setQuery}
        />
      </form>
      {error && <p className="m-0 text-sm text-scarlet">{error}</p>}
      {!items && !error && (
        <p className="m-0 text-sm text-ink-meta" role="status">
          Loading your photos.
        </p>
      )}
      {items && items.length === 0 && <p className="m-0 text-sm text-ink-secondary">No photos found.</p>}
      {items && items.length > 0 && (
        <ul aria-label="Penielly photos" className="m-0 grid list-none grid-cols-3 gap-1.5 p-0">
          {items.map((item) => {
            const on = picked.some((p) => p.id === item.id);
            const what = item.kind === "video" ? "Video" : "Photo";
            return (
              <li key={item.id}>
                <button
                  type="button"
                  aria-pressed={on}
                  aria-label={`${what} from ${takenOn(item.captured_at)}${item.place ? `, ${item.place}` : ""}`}
                  onClick={() => toggle(item)}
                  className={cn(
                    "relative block aspect-square w-full cursor-pointer overflow-hidden rounded-[10px] border-0 bg-surface-header p-0",
                    on && "outline-3 outline-offset-[-3px] outline-blue",
                    focusRing,
                  )}
                >
                  {item.thumb_url && (
                    <img
                      src={item.thumb_url}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  )}
                  {item.kind === "video" && (
                    <span className="absolute bottom-1 left-1 flex h-6 w-6 items-center justify-center rounded-full bg-linen text-ink">
                      <PlayIcon size={11} />
                    </span>
                  )}
                  {on && (
                    <span className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-blue text-white">
                      <CheckIcon size={14} strokeWidth={3} />
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {count >= MAX_PICK && (
        <p className="m-0 text-xs text-ink-meta">You can send up to {MAX_PICK} at a time.</p>
      )}
      <Button size="lg" block disabled={!count} onClick={() => onSend(picked)}>
        {count ? `Send ${count} to ${recipient}` : "Choose photos to send"}
      </Button>
      {back}
    </div>
  );
}
